/**
 * Live multi-patient central monitoring wall (Epic C4 / Gap #13).
 * 4–16 patient mosaic, latency SLAs, audible/visual alerts, ack audit.
 */

export const CENTRAL_MONITORING_WALL_SCHEMA_VERSION = "central-monitoring-wall-v1.0.0";

export const CENTRAL_MONITORING_WALL_SLAS = {
  mosaicLoadP95Ms: 3000,
  tileStatusRefreshMs: 2000,
  alertFanoutP95Ms: 1500,
  maxPatients: 16,
  minPatients: 4,
  layouts: [4, 8, 12, 16] as const,
};

export type WallLayoutSize = (typeof CENTRAL_MONITORING_WALL_SLAS.layouts)[number];

export type WallPatientTile = {
  tileId: string;
  studyId: string;
  patientLabel: string;
  status: "ok" | "warning" | "critical" | "offline";
  lastSampleAgeMs: number;
  pendingAlerts: number;
  jumpPath: string;
};

export type WallAlert = {
  alertId: string;
  studyId: string;
  severity: "info" | "warning" | "critical";
  message: string;
  raisedAtIso: string;
  audible: boolean;
  visual: boolean;
  acknowledged: boolean;
  acknowledgedAtIso: string | null;
  acknowledgedBy: string | null;
};

export type WallAlertAuditEntry = {
  atIso: string;
  actorId: string;
  action: "raise" | "ack" | "silence" | "export";
  alertId: string;
  studyId: string;
};

export type CentralMonitoringWall = {
  schemaVersion: string;
  layout: WallLayoutSize;
  tiles: WallPatientTile[];
  alerts: WallAlert[];
  audit: WallAlertAuditEntry[];
  degradedMode: boolean;
  degradedReason: string | null;
  labeling: string;
};

export const WALL_ALARM_FATIGUE_CONTROLS = {
  riskId: "NDX-RISK-EEG-WALL-ALARM-FATIGUE-001",
  requireAckForCritical: true,
  noClosedLoopTherapy: true,
  stormDegradeThreshold: 40,
};

let alertSeq = 0;

export function createMonitoringWall(input: {
  patients: Array<{
    studyId: string;
    patientLabel: string;
    status?: WallPatientTile["status"];
    lastSampleAgeMs?: number;
    pendingAlerts?: number;
  }>;
  layout?: WallLayoutSize;
}): CentralMonitoringWall {
  const requested = input.layout ?? (input.patients.length <= 4
    ? 4
    : input.patients.length <= 8
      ? 8
      : input.patients.length <= 12
        ? 12
        : 16);
  const layout = CENTRAL_MONITORING_WALL_SLAS.layouts.includes(requested)
    ? requested
    : 16;
  const capped = input.patients.slice(0, layout);
  const tiles: WallPatientTile[] = capped.map((p, i) => ({
    tileId: `tile-${i + 1}`,
    studyId: p.studyId,
    patientLabel: p.patientLabel,
    status: p.status ?? "ok",
    lastSampleAgeMs: p.lastSampleAgeMs ?? 500,
    pendingAlerts: p.pendingAlerts ?? 0,
    jumpPath: `/monitoring/icu/${encodeURIComponent(p.studyId)}`,
  }));
  const stale = tiles.some(
    (t) => t.lastSampleAgeMs > CENTRAL_MONITORING_WALL_SLAS.tileStatusRefreshMs * 5,
  );
  return {
    schemaVersion: CENTRAL_MONITORING_WALL_SCHEMA_VERSION,
    layout,
    tiles,
    alerts: [],
    audit: [],
    degradedMode: stale || tiles.length === 0,
    degradedReason: tiles.length === 0
      ? "no_patients"
      : stale
        ? "stale_tiles"
        : null,
    labeling:
      "Central monitoring wall is assistive visualization. No closed-loop therapy. Critical alerts require acknowledgment.",
  };
}

export function raiseWallAlert(
  wall: CentralMonitoringWall,
  input: {
    studyId: string;
    severity: WallAlert["severity"];
    message: string;
    actorId?: string;
    now?: Date;
  },
): CentralMonitoringWall {
  alertSeq += 1;
  const atIso = (input.now ?? new Date()).toISOString();
  const alert: WallAlert = {
    alertId: `wall-alert-${alertSeq}`,
    studyId: input.studyId,
    severity: input.severity,
    message: input.message,
    raisedAtIso: atIso,
    audible: input.severity !== "info",
    visual: true,
    acknowledged: false,
    acknowledgedAtIso: null,
    acknowledgedBy: null,
  };
  const alerts = [...wall.alerts, alert];
  const storm = alerts.filter((a) => !a.acknowledged).length >=
    WALL_ALARM_FATIGUE_CONTROLS.stormDegradeThreshold;
  return {
    ...wall,
    alerts,
    degradedMode: wall.degradedMode || storm,
    degradedReason: storm ? "alert_storm" : wall.degradedReason,
    audit: [
      ...wall.audit,
      {
        atIso,
        actorId: input.actorId ?? "system",
        action: "raise",
        alertId: alert.alertId,
        studyId: input.studyId,
      },
    ],
  };
}

export function acknowledgeWallAlert(
  wall: CentralMonitoringWall,
  alertId: string,
  actorId: string,
  now?: Date,
): CentralMonitoringWall {
  const atIso = (now ?? new Date()).toISOString();
  const alerts = wall.alerts.map((a) =>
    a.alertId === alertId
      ? {
          ...a,
          acknowledged: true,
          acknowledgedAtIso: atIso,
          acknowledgedBy: actorId,
        }
      : a,
  );
  const target = wall.alerts.find((a) => a.alertId === alertId);
  if (!target) return wall;
  return {
    ...wall,
    alerts,
    audit: [
      ...wall.audit,
      {
        atIso,
        actorId,
        action: "ack",
        alertId,
        studyId: target.studyId,
      },
    ],
  };
}

export function exportWallAlertAudit(wall: CentralMonitoringWall): WallAlertAuditEntry[] {
  return [...wall.audit];
}

export function estimateWallLoadEvidence(input: {
  concurrentPatients: number;
  alertRatePerMin: number;
}): {
  withinSla: boolean;
  estimatedMosaicLoadMs: number;
  estimatedFanoutMs: number;
  policy: string;
} {
  const n = Math.max(0, input.concurrentPatients);
  const mosaic = 400 + n * 120;
  const fanout = 200 + input.alertRatePerMin * 15 + n * 40;
  return {
    withinSla:
      mosaic <= CENTRAL_MONITORING_WALL_SLAS.mosaicLoadP95Ms &&
      fanout <= CENTRAL_MONITORING_WALL_SLAS.alertFanoutP95Ms,
    estimatedMosaicLoadMs: mosaic,
    estimatedFanoutMs: fanout,
    policy:
      "On SLA miss: degrade to status-only tiles, suppress non-critical audible, require ack for critical.",
  };
}
